import { renderNavbar } from '../components/navbar.js';
import { supabase, isSupabaseReady } from '../services/supabase.js';
import { escapeHtml } from '../utils/html.js';
import { buildPlatformShareUrl, buildSocialSharePayload, listSocialPlatforms } from '../utils/social-share.js';
import { generateSocialShareImage } from '../utils/social-image.js';
import { tr } from '../services/i18n.js';

export async function renderShare(container, params = {}) {
  const debriefId = cleanId(params.id);
  container.innerHTML = `
    <div class="page p-16" style="padding-bottom:100px;">
      <div class="app-header">
        <div class="brand">
          <div class="brand-mark">FV</div>
          <div>
            <p class="brand-title">${tr('Preview social', 'Social preview')}</p>
            <p class="brand-subtitle">${tr('Image + texte par plateforme', 'Image + text by platform')}</p>
          </div>
        </div>
        <a class="btn-secondary" href="#/debrief/${escapeHtml(debriefId)}">${tr('Débrief', 'Debrief')}</a>
      </div>
      <div id="share-content"><div class="glass-card skeleton" style="height:260px;"></div></div>
    </div>
  `;
  renderNavbar(container);

  const content = container.querySelector('#share-content');
  if (!debriefId || !isSupabaseReady()) {
    content.innerHTML = `<div class="glass-card"><p class="text-muted">${tr('Partage indisponible.', 'Sharing unavailable.')}</p></div>`;
    return;
  }

  try {
    let { data, error } = await supabase
      .from('debriefs')
      .select(`
        *,
        competition:competitions (id, name, code),
        match:matches (id, home_team, away_team, kickoff_at),
        user:profiles!debriefs_user_id_fkey (id, pseudo, avatar_url, avatar_emoji)
      `)
      .eq('id', debriefId)
      .single();

    if (error && /competition|match|relationship|column/i.test(error.message || '')) {
      const retry = await supabase
        .from('debriefs')
        .select('*, user:profiles!debriefs_user_id_fkey (id, pseudo, avatar_url, avatar_emoji)')
        .eq('id', debriefId)
        .single();
      data = retry.data;
      error = retry.error;
    }
    if (error || !data) throw error || new Error(tr('Débrief introuvable', 'Debrief not found'));

    content.innerHTML = `
      <div class="flex-col gap-16">
        <div class="glass-card">
          <p class="eyebrow">${tr('Asset social', 'Social asset')}</p>
          <h1 class="heading-2" style="margin-bottom:12px;">${escapeHtml(data.title)}</h1>
          <div class="actions">
            <select id="share-platform" class="input-glow" style="flex:1;min-width:190px;">
              ${listSocialPlatforms().map(p => `<option value="${escapeHtml(p.id)}">${escapeHtml(p.label)}</option>`).join('')}
            </select>
            <button id="btn-render-share" class="btn-primary" type="button">${tr('Générer', 'Generate')}</button>
            <button id="btn-copy-share" class="btn-secondary" type="button">${tr('Copier texte', 'Copy text')}</button>
            <button id="btn-open-share" class="btn-secondary" type="button">${tr('Poster', 'Post')}</button>
          </div>
        </div>
        <div id="share-output" class="glass-card">
          <p class="text-muted" style="margin:0;">${tr('Choisis une plateforme puis génère l’aperçu.', 'Choose a platform, then generate the preview.')}</p>
        </div>
      </div>
    `;

    const platformSelect = content.querySelector('#share-platform');
    const output = content.querySelector('#share-output');
    const render = async () => {
      const payload = buildSocialSharePayload(data, { platform: platformSelect.value, url: window.location.href.replace('#/share/', '#/debrief/') });
      output.innerHTML = '<div class="skeleton" style="height:220px;"></div>';
      const imageUrl = await generateSocialShareImage(data, { platform: platformSelect.value, url: payload.url });
      output.innerHTML = `
        <p class="text-muted" style="font-size:12px;margin:0 0 8px;">${escapeHtml(payload.platform.label)} · ${escapeHtml(payload.format.width)}x${escapeHtml(payload.format.height)}</p>
        <img src="${imageUrl}" alt="${tr('Aperçu social', 'Social preview')}" style="width:100%;max-height:640px;object-fit:contain;border-radius:8px;border:1px solid var(--line);background:var(--surface-2);" />
        <p style="white-space:pre-wrap;font-size:13px;line-height:1.5;margin:12px 0;">${escapeHtml(payload.text)}</p>
        <button id="btn-download-preview" class="btn-primary" type="button" style="width:100%;">${tr('Télécharger le PNG', 'Download PNG')}</button>
      `;
      output.querySelector('#btn-download-preview').addEventListener('click', () => downloadDataUrl(imageUrl, `fooview-${payload.platform.id}.png`));
    };

    content.querySelector('#btn-render-share').addEventListener('click', () => render().catch((e) => showToast(tr('Aperçu impossible: ', 'Preview failed: ') + e.message)));
    content.querySelector('#btn-copy-share').addEventListener('click', async () => {
      const payload = buildSocialSharePayload(data, { platform: platformSelect.value, url: window.location.href.replace('#/share/', '#/debrief/') });
      await navigator.clipboard.writeText(payload.text);
      showToast(tr('Texte copié.', 'Text copied.'));
    });
    content.querySelector('#btn-open-share').addEventListener('click', () => {
      const payload = buildSocialSharePayload(data, { platform: platformSelect.value, url: window.location.href.replace('#/share/', '#/debrief/') });
      const shareUrl = buildPlatformShareUrl(payload.platform.id, { text: payload.text, url: payload.url });
      if (shareUrl) window.open(shareUrl, '_blank', 'noopener,noreferrer');
      else showToast(tr('Copie le texte et télécharge l’image pour cette plateforme.', 'Copy the text and download the image for this platform.'));
    });
    await render();
  } catch (e) {
    content.innerHTML = `<div class="glass-card"><p class="text-muted">${tr('Erreur', 'Error')}: ${escapeHtml(e.message)}</p></div>`;
  }
}

function cleanId(value = '') {
  return String(value).split('?')[0].trim();
}

function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function showToast(msg) {
  const t = document.createElement('div');
  t.className = 'toast show';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => { t.className = 'toast'; setTimeout(() => t.remove(), 300); }, 3000);
}
